import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import axios from "axios";
import {useTranslation} from "react-i18next";

import {
    LinearProgress,
    styled,
    Grid,
    Typography,
    Card,
    CardHeader,
    CardContent,
    CardMedia,
    Modal,
    Backdrop,
    Fade,
} from "@mui/material";

import useDataSource from "../../../../hooks/useDataSource";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";

import {
    formatDate,
    formatDuration,
    formatNumber,
    formatStyle,
    formatText,
    formatSuffix
} from "../../../../utilities/FormatUtilities";

import {
    getFieldFormatters
} from "../../../../utilities/DataSourceUtilities";

import NoImage from "../../../../styles/images/NoProjectImageFound.jpg";
import {findComponent} from "../../../../redux/page/pageActions";

const PREFIX = 'KeyAttributes';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    cardHeader: `${PREFIX}-cardHeader`,
    cardContent: `${PREFIX}-cardContent`,
    attribute: `${PREFIX}-attribute`,
    cardMedia: `${PREFIX}-cardMedia`,
    cardMediaLoading: `${PREFIX}-cardMediaLoading`,
    loadingContainer: `${PREFIX}-loadingContainer`,
    modal: `${PREFIX}-modal`,
    modalPaper: `${PREFIX}-modalPaper`,
    cardMediaModal: `${PREFIX}-cardMediaModal`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.card}`]: {
        boxShadow: 'none',
        height: '100%',
        overflow: 'auto'
    },

    [`& .${classes.cardHeader}`]: {
        height: '68px'
    },

    [`& .${classes.cardContent}`]: {
        // height: 'calc(100% - 182px)',
        height: '100%',
        padding: '16px',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'left'
    },

    [`&.${classes.attribute}`]: {
        width: '115px',
        margin: 'auto'
    },

    [`& .${classes.cardMedia}`]: {
        // height: 'calc(100% - 53px - 68px - 40px)',
        height: '114px',
        cursor: 'pointer'
    },

    [`& .${classes.cardMediaLoading}`]: {
        position: 'relative',
        width: '100%',
        // height: 'calc(100% - 53px - 68px - 40px)',
        height: '114px'
    },

    [`& .${classes.loadingContainer}`]: {
        width: '100%',
        // height: '100%',
        height: '114px',
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        margin: '0px'
    },

    [`& .${classes.modal}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    [`& .${classes.modalPaper}`]: {
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        height: '50%',
        width: '50%',
        padding: theme.spacing(2, 4, 3),
    },

    [`& .${classes.cardMediaModal}`]: {
        // height: 'calc(100% - 53px - 68px - 40px)',
        height: '100%',
        width: '100%'
    }
}));

export default function KeyAttributes({id, configuration, xId}) {

	const dispatch = useDispatch();
	const { t } = useTranslation();


    // App State
    // const auth = useSelector(state => state.auth);
    const config = useSelector(state => state.config);
    const data = useSelector(state => state.data);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [attributeList, setAttributes] = useState([]);
    const [showProgress, setShowProgress] = useState(compConfig.element?.showFetching !== undefined ? compConfig.element?.showFetching : true);
    const [images, setImages] = useState(null);
    const [projectImageLoading, setImageLoading] = useState(true);
    const [openImageModal, setImageModalOpen] = useState(false);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

	const styles = compConfig?.element?.styles;
	const includeImage = compConfig.element?.includeImage !== undefined ? compConfig.element.includeImage : true;
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource.name : '';
	const fields = compConfig.dataSource?.fields ? compConfig.dataSource.fields : '';

	const dataSource = useDataSource(dataSourceName);

    const handleImageModalOpen = () => {
        setImageModalOpen(true);
    };

    const handleImageModalClose = () => {
        setImageModalOpen(false);
    };

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// Titles & Resized
	// componentUpdated & Resized

	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Key Attributes Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'KeyAttributes'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);


    // Data
    useEffect(() => {
        if (dataSource.data && dataSource.fetched && fields && dataSource.data.length > 0) {
            const compiledAttributesArray = [];

            const componentTitle = dataSource.data[0][title] ? dataSource.data[0][title] : title;
            const componentSubTitle = dataSource.data[0][subtitle] ? dataSource.data[0][subtitle] : subtitle;
            setTitle(componentTitle);
			setSubTitle(componentSubTitle)

            // TODO convert to Declarative by using .map and calling a formatting function
            for (let p = 0; p < fields.length; p++) {
                const attr = fields[p];

                // Format Value
                const formatters = getFieldFormatters(dataSource, attr.name); // Used when formatters are on the data source
                // const formatters = attr.formatters; // used when formatters are on the component

                let formattedValue = dataSource.data[0][attr.name] ? dataSource.data[0][attr.name] : "";
                for (const formatter of formatters) {
                    if (formatter.type.toUpperCase() === 'DATE') {
                        formattedValue = formatDate(formattedValue, formatter.config);
                        // formattedValue = DateUtilities.dateMonthYearString(formattedValue);
                    } else if ((formatter.type.toUpperCase() === 'DURATION') && (dataSource.name.toUpperCase() === 'DURATION')) {
                        formattedValue = formatDuration(dataSource.data[0].min, dataSource.data[0].max, formatter.config);
                    } else if (formatter.type.toUpperCase() === 'NUMBER') {
                        formattedValue = formatNumber(formattedValue, formatter.config);
                    } else if (formatter.type.toUpperCase() === 'STYLE') {
                        formattedValue = formatStyle(formattedValue, formatter.config);
                    } else if (formatter.type.toUpperCase() === 'TEXT') {
                        formattedValue = formatText(formattedValue, formatter.config);
                    } else if (formatter.type.toUpperCase() === 'SUFFIX') {
                        formattedValue = formatSuffix(formattedValue, formatter.config);
                    } else {

                    }
                }

                compiledAttributesArray.push(
                    <Root key={p} className={classes.attribute}>
                        <Typography variant="body2" display="inline" gutterBottom align="left">
                            {attr.label}
                        </Typography>
                        <Typography variant="caption" display="inline" gutterBottom style={{display: 'flex', alignItems: 'left', justifyContent: 'left'}}>
                            {formattedValue}
                        </Typography>
                    </Root>
                );
            }
            setAttributes(compiledAttributesArray);

        } else {
            setAttributes([]);

        }
    }, [dataSource.data]);


    // Get images
    useEffect(() => {
        if (dataSource.data && includeImage) {
            const url = config.data.authentication.api + 'DBQuery';

            axios.get(url, {
                params: {
                    db: config.data.authentication.apiSource,
                    table: 'ProjectImage{0}'.format(data.viewStaged ? 'Staged': ''),
                    fields: "SMImage",
                    where: "Identifier eq '" + dataSource.data[0].id + "'"
                },
                useCache: true
            })
                .then(function (response) {
                    const smImageBase64 = 'data:image/png;base64,' + response.data[0].smImage;
                    const lgImageBase64 = 'data:image/png;base64,' + response.data[0].lgImage;
                    setImages({smImage: smImageBase64, lgImage: lgImageBase64});
                })
                .catch(function (error) {
                    setImages(NoImage);
                });
        }
    }, [dataSource.data]);

    // Set projectImageLoading to false once image value is returned
    useEffect(() => {
        // console.log('ProjectCard: UseEffect, [image]');
        if (includeImage) {
            if (images) {
                setImageLoading(false);
            } else {
                setImageLoading(true);
            }
        }
    }, [images]);

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'
		});

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <div className={classes.linearProgress}/>
            }
            <Card className={classes.card} style={{...styles?.card}}>
                <CardHeader className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'h6'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}/>
                {includeImage && projectImageLoading &&
                <div className={classes.cardMediaLoading}>
                    {/*<CircularProgress className={classes.cardMediaProgress}/>
                                        TODO: REPLACE WITH STATIC LOADING IMAGE SO BROWSER DOESNT HAVE TO WORK ON ANI */}
                    <div className={classes.loadingContainer}>
                        <Typography variant="subtitle2" gutterBottom style={{textAlign: 'center'}}>
                            LOADING...
                        </Typography>
                    </div>
                </div>
                }
                {includeImage && !projectImageLoading &&
                <React.Fragment>
                    <CardMedia className={classes.cardMedia} onClick={handleImageModalOpen} image={images.smImage} title={dataSource.data[0][fields[0].name]}/>
                    <Modal
                        aria-labelledby="transition-modal-title"
                        aria-describedby="transition-modal-description"
                        className={classes.modal}
                        open={openImageModal}
                        onClose={handleImageModalClose}
                        closeAfterTransition
                        BackdropComponent={Backdrop}
                        BackdropProps={{
                            timeout: 500,
                        }}
                    >
                        <Fade in={openImageModal}>
                            <div className={classes.modalPaper}>
                                <CardMedia className={classes.cardMediaModal} image={images.lgImage} title={dataSource.data[0][fields[0].name]}/>
                            </div>
                        </Fade>
                    </Modal>
                </React.Fragment>
                }
                <CardContent className={classes.cardContent} style={cardContentStyle}>
                    <Grid container justifyContent={"center"} spacing={0}>
                        {attributeList.map((a,i) => {
                                return (
                                    <Grid key={'{0}-{1}'.format(window.dw.utils.newID(), i)} item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        {a}
                                    </Grid>
                                );
                            })
                        }
                    </Grid>
                </CardContent>
            </Card>

        </Root>
    );
}
